import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

class Browser {
  // if iOS, return true
  // https://stackoverflow.com/a/9039885/779803
  static isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  }

  /**
   * This is the case where we don't want to use mock-m2m,
   * but we also aren't using the real endpoints.
   * This encompasses some types of local development and the preview expectations.
   * @returns {boolean}
   */
  static simulateSdk() {
    const payload = Browser.getParameterValue('payload');
    return (!Browser.isMobile() && !Browser.useMockM2M()) || payload === 'preview';
  }

  static getScreenSize = () => {
    // Get the device pixel ratio
    const ratio = window.devicePixelRatio || 1;

    // Define the users device screen dimensions
    return {
      width: window.screen.width * ratio,
      height: window.screen.height * ratio,
    };
  };

  static getHeaderHeight() {
    return 54;
  }

  static getTopPadding() {
    if (Browser.isIOS()) {
      const topSafeArea = getComputedStyle(document.documentElement).getPropertyValue("--sat");
      //console.log('>>getTopPadding  topSafeArea', topSafeArea);
      const screen = this.getScreenSize();
      // ES-24239
      //check for safearea and return
      if (topSafeArea) {
        // iphoneX
        //console.log('>>getTopPadding  topSafeArea return', parseInt(topSafeArea, 10));
        return parseInt(topSafeArea, 10);
      }
      // default status bar size
      //console.log('>>getTopPadding  return 20');
      return 20;
    }
    return 0;
  }

  static isIphoneX() {
    const isIOS = Browser.isIOS();
    const screen = this.getScreenSize();

    // iPhone X Detection
    return isIOS && screen.width === 1125 && screen.height === 2436;
  }

  static generateCustomUrl(target, customScheme) {
    console.log('generateCustomUrl');
    console.log("target");
    console.log(target);
    console.log("customScheme");
    console.log(customScheme);
    let localCustomScheme = customScheme;
    // if previewing in the browser and not on a device, don't use m2m: protocol as most browser will barf on it
    // preview via page https://m2mcms.inmarket.com/campaigns/edit-ad?id=2338
    const url = window.location.href;
    console.log('url', url);
    const preview = Browser.getParameterValue('preview_in_browser', url);
    console.log('preview', typeof preview, 'length', preview.length, preview);
    if (preview) {
      console.log('returning # for url:', url);
      return '#';
    }

    localCustomScheme = localCustomScheme || 'm2m://interstitial';
    if (localCustomScheme.substr(-1) === '/') {
      localCustomScheme = localCustomScheme.substr(0, localCustomScheme.length - 1);
    }
    console.log('------ returning generateCustomUrl');
    console.log('localCustomScheme', localCustomScheme);
    console.log('target', target);
    return `${localCustomScheme}/${target}`;
  }

  static getQueryString(url) {
    const parts = url.split('?');
    if (parts.length > 1) {
      return parts[1];
    }
    return '';
  }

  static getParameterValue(name, url) {
    const localUrl = url || window.location.search;
    let localName = name;
    try {
      localName = localName.replace(/[[]/, '\\[').replace(/[\]]/, '\\]');
      const regex = new RegExp(`[\\?&]${localName}=([^&#]*)`);
      const results = regex.exec(localUrl);
      return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
    } catch (e) {
      return '';
    }
  }

  /**
   * Get the browser host only
   * Defaults to CDN removal
   *
   * @param url
   * @param removeCDN
   * @returns {string | void}
   */
  static getHost(url, removeCDN = true) {
    let domain = '';
    let localUrl = url;
    if (!localUrl) localUrl = get(window, 'location.href');
    // find & remove protocol (http, ftp, etc.) and get domain
    if (localUrl.indexOf('://') > -1) {
      domain = localUrl
        .split('/')
        .slice(0, 3)
        .join('/');
    } else {
      // eslint-disable-next-line
      domain = localUrl.split('/')[0];
    }
    // Remove cdn.
    return removeCDN ? domain.replace('cdn-', '') : domain;
  }

  static getDomain(url) {
    let domain;
    // find & remove protocol (http, ftp, etc.) and get domain
    if (url.indexOf('://') > -1) {
      domain = url.split('/').slice(1, 3);
    } else {
      domain = url.split('/')[0];
    }
    // console.log('Browser.getDomain: ', domain);
    return domain;
  }

  static sharesThisHost(url) {
    return this.getHost(url) === this.getHost(window.location.href);
  }

  static validURL(str) {
    if (typeof str !== 'string') {
      return false;
    }
    let localStr = str;
    localStr = localStr.split('?').shift();
    return /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?(\?.*)?$/.test(
      localStr.trim()
    );
  }

  static isMobile(device) {
    let isMobile = true;
    const ua = navigator.userAgent;
    if (navigator.platform.substr(0, 3) === 'Mac' || navigator.platform.substr(0, 3) === 'Win') {
      isMobile = false;
    } else if (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile/i.test(ua)
    ) {
      isMobile = true;
    } else if (/Chrome/i.test(ua)) {
      isMobile = true;
    } else {
      isMobile = false;
    }
    console.log(`is mobile: ${isMobile} platform: ${navigator.platform.substr(0, 3)}`);

    return device === true ? navigator.platform.substr(0, 3) : isMobile;
  }

  static isDevelopment() {
    return (
      process.env.NODE_ENV === 'development' ||
      get(window, 'location.href', '').match(/^http[s]*:\/\/(?:qa[0-9]*|local)[-]*/) !== null
    );
  }

  /**
   * @see https://bitbucket.org/inmarketmedia/m2m-third-party/src/master/README.md
   * @returns {string}
   */
  static thirdPartyPels = () => {
    return `${this.scriptHost()}/js/third_party_impr_pels.js?ver=1557346362`;
  }

  static imHtmlNode = () => document.getElementById('imHtml');

  static celtraContainerNode = () => document.getElementById('contentNodeCeltra');

  static domContainerNode = () => document.getElementById('root');

  static scriptContainerNode = () => document.getElementById('pelsAndScripts') || document.body;

  static scriptHost = () => {
    let ret = '';
    if (this.useMockM2M()) {
      ret = `${process.env.REACT_APP_MOCK_M2M_HOST}/api-requests`;
    } else {
      let env = this.getEnvironment();
      env = env === 'production' ? '' : env;
      ret = `${env ? `${env}-` : ''}cdn-m2m-api.inmarket.com`;
    }
    return ret;
  }

  static mockM2MEndpoint = () => `//${process.env.REACT_APP_MOCK_M2M_HOST}/sdk-callbacks`;

  static useMockM2M = () => typeof process.env.REACT_APP_MOCK_M2M_HOST !== 'undefined';

  /**
   * False if we're not using mock m2m and in NODE_ENV == development
   * @returns {boolean}
   */
  static isLocal = () => !this.useMockM2M() && process.env.NODE_ENV === 'development';

  static getEnvironment() {
    const full = this.getHost(window.location.host, true);
    const parts = full.split('.');
    let sub = parts[0];

    sub = sub.replace('-engagement', '');
    if (sub === 'engagement') return 'production';
    if (sub === 'local') return 'qa3';

    return sub;
  }

  static getScreenDetails = () => {
    const unknown = '-';

    // screen
    let screenSize = '';
    let width;
    let height;
    const { screen } = window;
    if (screen.width) {
      width = screen.width ? screen.width : '';
      height = screen.height ? screen.height : '';
      screenSize += `${width} x ${height}`;
    }

    // browser
    const nVer = navigator.appVersion;
    const nAgt = navigator.userAgent;
    let browser = navigator.appName;
    let version = `${parseFloat(navigator.appVersion)}`;
    let majorVersion = parseInt(navigator.appVersion, 10);
    let nameOffset;
    let verOffset;
    let ix;

    // Opera
    if ((verOffset = nAgt.indexOf('Opera')) !== -1) {
      browser = 'Opera';
      version = nAgt.substring(verOffset + 6);
      if ((verOffset = nAgt.indexOf('Version')) !== -1) {
        version = nAgt.substring(verOffset + 8);
      }
    }
    // Opera Next
    if ((verOffset = nAgt.indexOf('OPR')) !== -1) {
      browser = 'Opera';
      version = nAgt.substring(verOffset + 4);
    }
    // Edge
    else if ((verOffset = nAgt.indexOf('Edge')) !== -1) {
      browser = 'Microsoft Edge';
      version = nAgt.substring(verOffset + 5);
    }
    // MSIE
    else if ((verOffset = nAgt.indexOf('MSIE')) !== -1) {
      browser = 'Microsoft Internet Explorer';
      version = nAgt.substring(verOffset + 5);
    }
    // Chrome
    else if ((verOffset = nAgt.indexOf('Chrome')) !== -1) {
      browser = 'Chrome';
      version = nAgt.substring(verOffset + 7);
    }
    // Safari
    else if ((verOffset = nAgt.indexOf('Safari')) !== -1) {
      browser = 'Safari';
      version = nAgt.substring(verOffset + 7);
      if ((verOffset = nAgt.indexOf('Version')) !== -1) {
        version = nAgt.substring(verOffset + 8);
      }
    }
    // Firefox
    else if ((verOffset = nAgt.indexOf('Firefox')) !== -1) {
      browser = 'Firefox';
      version = nAgt.substring(verOffset + 8);
    }
    // MSIE 11+
    else if (nAgt.indexOf('Trident/') !== -1) {
      browser = 'Microsoft Internet Explorer';
      version = nAgt.substring(nAgt.indexOf('rv:') + 3);
    }
    // Other browsers
    else if ((nameOffset = nAgt.lastIndexOf(' ') + 1) < (verOffset = nAgt.lastIndexOf('/'))) {
      browser = nAgt.substring(nameOffset, verOffset);
      version = nAgt.substring(verOffset + 1);
      if (browser.toLowerCase() === browser.toUpperCase()) {
        browser = navigator.appName;
      }
    }
    // trim the version string
    if ((ix = version.indexOf(';')) !== -1) version = version.substring(0, ix);
    if ((ix = version.indexOf(' ')) !== -1) version = version.substring(0, ix);
    if ((ix = version.indexOf(')')) !== -1) version = version.substring(0, ix);

    majorVersion = parseInt(`${version}`, 10);
    if (isNaN(majorVersion)) {
      version = `${parseFloat(navigator.appVersion)}`;
      majorVersion = parseInt(navigator.appVersion, 10);
    }

    // mobile version
    const mobile = /Mobile|mini|Fennec|Android|iP(ad|od|hone)/.test(nVer);

    // cookie
    let cookieEnabled = !!navigator.cookieEnabled;

    if (typeof navigator.cookieEnabled === 'undefined' && !cookieEnabled) {
      document.cookie = 'testcookie';
      cookieEnabled = document.cookie.indexOf('testcookie') !== -1;
    }

    // system
    let os = unknown;
    const clientStrings = [
      { s: 'Windows 10', r: /(Windows 10.0|Windows NT 10.0)/ },
      { s: 'Windows 8.1', r: /(Windows 8.1|Windows NT 6.3)/ },
      { s: 'Windows 8', r: /(Windows 8|Windows NT 6.2)/ },
      { s: 'Windows 7', r: /(Windows 7|Windows NT 6.1)/ },
      { s: 'Windows Vista', r: /Windows NT 6.0/ },
      { s: 'Windows Server 2003', r: /Windows NT 5.2/ },
      { s: 'Windows XP', r: /(Windows NT 5.1|Windows XP)/ },
      { s: 'Windows 2000', r: /(Windows NT 5.0|Windows 2000)/ },
      { s: 'Windows ME', r: /(Win 9x 4.90|Windows ME)/ },
      { s: 'Windows 98', r: /(Windows 98|Win98)/ },
      { s: 'Windows 95', r: /(Windows 95|Win95|Windows_95)/ },
      { s: 'Windows NT 4.0', r: /(Windows NT 4.0|WinNT4.0|WinNT|Windows NT)/ },
      { s: 'Windows CE', r: /Windows CE/ },
      { s: 'Windows 3.11', r: /Win16/ },
      { s: 'Android', r: /Android/ },
      { s: 'Open BSD', r: /OpenBSD/ },
      { s: 'Sun OS', r: /SunOS/ },
      { s: 'Linux', r: /(Linux|X11)/ },
      { s: 'iOS', r: /(iPhone|iPad|iPod)/ },
      { s: 'Mac OS X', r: /Mac OS X/ },
      { s: 'Mac OS', r: /(MacPPC|MacIntel|Mac_PowerPC|Macintosh)/ },
      { s: 'QNX', r: /QNX/ },
      { s: 'UNIX', r: /UNIX/ },
      { s: 'BeOS', r: /BeOS/ },
      { s: 'OS/2', r: /OS\/2/ },
      {
        s: 'Search Bot',
        r: /(nuhk|Googlebot|Yammybot|Openbot|Slurp|MSNBot|Ask Jeeves\/Teoma|ia_archiver)/,
      },
    ];
    for (const id in clientStrings) {
      const cs = clientStrings[id];
      if (cs.r.test(nAgt)) {
        os = cs.s;
        break;
      }
    }

    let osVersion = unknown;

    if (/Windows/.test(os)) {
      osVersion = /Windows (.*)/.exec(os)[1];
      os = 'Windows';
    }

    switch (os) {
      case 'Mac OS X':
        osVersion = /Mac OS X (10[._\d]+)/.exec(nAgt)[1];
        break;

      case 'Android':
        osVersion = /Android ([._\d]+)/.exec(nAgt)[1];
        break;

      case 'iOS':
        osVersion = /OS (\d+)_(\d+)_?(\d+)?/.exec(nVer);
        osVersion = `${osVersion[1]}.${osVersion[2]}.${osVersion[3] | 0}`;
        break;
      default:
        osVersion = 'N/A';
    }

    // flash (you'll need to include swfobject)
    /* script src="//ajax.googleapis.com/ajax/libs/swfobject/2.2/swfobject.js" */
    let flashVersion = 'no check';
    if (typeof window.swfobject !== 'undefined') {
      const fv = window.swfobject.getFlashPlayerVersion();
      if (fv.major > 0) {
        flashVersion = `${fv.major}.${fv.minor} r${fv.release}`;
      } else {
        flashVersion = unknown;
      }
    }

    return {
      screen: screenSize,
      browser,
      browserVersion: version,
      browserMajorVersion: majorVersion,
      mobile,
      os,
      osVersion,
      cookies: cookieEnabled,
      flashVersion,
    };
  };

  static shouldSendEngagementLog(url) {
    const validUrl = url && this.validURL(url) ? true : window.location.search;
    const matchThis = url || window.location.search;
    return validUrl && matchThis.match(/[?&#]m2m_server_logging=1/);
  }

  static shouldShowMoatDebug() {
    return get(window, 'location.href', '').match(/moat=debug/)  !== null || this.isDevelopment();
  }

  static isUrlMoatPixel(url) {
    console.log(`isUrlMoatPixel(${url})`);
    if(isEmpty(url)) return false;
    return (
      typeof url === 'string' &&
      this.validURL(url) &&
      url.indexOf('moat') > -1 &&
      url.indexOf('pixel.gif') > -1
    );
  }

  static isUrlInHandPixel(url) {
    console.log(`isUrlInHandPixel(${url})`);
    if(isEmpty(url)) return false;
    const urle = new URL(url);
    const urlPathParts = urle.pathname.split('/');
    const urlQ = new URLSearchParams(urle.search);
    if (
      urlPathParts[1] === 'product' &&
      urlPathParts[3] === 'scan' &&
      urlPathParts[5] === 'impression' &&
      urlQ.has('impression_id')
    ) {
      return true;
    }
    return false;
  }

  static clearCeltraNodes() {
    const notranslates = document.querySelectorAll('body > div.notranslate');
    if (notranslates.length) {
      for(let i = 0; i < notranslates.length; i++) {
        document.body.removeChild(notranslates.item(i));
      }
    }
  }
}

export default Browser;
